import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "asset-search-container" }
const _hoisted_2 = { class: "d-flex flex-column justify-center flex-grow-1" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = {
  key: 1,
  class: "no-results"
}

import {computed, nextTick, onMounted, ref, watch} from 'vue'
import {AssetFetcher, AssetItem} from "@/types/Asset";

const PAGE_SIZE = 30

export default /*@__PURE__*/_defineComponent({
  __name: 'AssetSearch',
  props: /*@__PURE__*/_mergeModels({
    fetchItems: { type: Function },
    assetKeys: {},
    items: {},
    onSubmit: { type: Function },
    onSetting: { type: Function },
    placeholder: {}
  }, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any) {

const model = _useModel(__props, "modelValue");

/*
* model["id"] = asset["id"]
* */

const localModel = ref<string>("");

const props = __props;

const internalItems = ref<AssetItem[]>([])  // Geladene Items
const totalItems = ref(0)                    // Server-Total
const isLoadingAssets = ref(false)
const currentOffset = ref(0)
const searchDebounceTimer = ref<number | null>(null)

const isSearchActive = ref(false)
const showSuggestions = ref(false)
const selectedIndex = ref(-1)
const lastIndex = ref(10);
const infiniteScrollKey = ref(0); // Key für Force-Re-render

// Computed properties
/*const filteredAssets = computed(() => {
  if (model.value == null) return props.items.slice(0, lastIndex.value);

  return props.items.filter(asset =>
      asset.title.toLowerCase().includes(model.value.toLowerCase()) ||
      asset.subtitle.toLowerCase().includes(model.value.toLowerCase())
  ).slice(0, lastIndex.value);
})*/

const filteredAssets = computed(() => {
  if (props.fetchItems) {
    return internalItems.value  // Server hat schon gefiltert
  }
  return []
  // Alter Modus: lokale Filterung (backward-compat)
  /*  if (localModel.value == null) return (props.items ?? []).slice(0, lastIndex.value)
    return (props.items ?? []).filter(asset =>
        asset.title.toLowerCase().includes(localModel.value.toLowerCase()) ||
        asset.subtitle.toLowerCase().includes(localModel.value.toLowerCase())
    ).slice(0, lastIndex.value)*/
})

const hasMoreItems = computed(() => currentOffset.value < totalItems.value)

// Watch für showSuggestions um infinite-scroll zurückzusetzen
watch(showSuggestions, (newVal) => {
  if (newVal) {
    // Reset lastIndex when opening suggestions
    lastIndex.value = 10;
    // Force re-render of infinite scroll
    infiniteScrollKey.value++;
  }
});

onMounted(() => {
  if (props.fetchItems) {
    loadPage(0, '')  // Erste Seite im Hintergrund laden
  }
})

async function loadPage(offset: number, search: string) {
  if (isLoadingAssets.value) return
  isLoadingAssets.value = true
  try {
    const result = await props.fetchItems!({offset, limit: PAGE_SIZE, search})
    if (offset === 0) {
      internalItems.value = result.items       // Reset bei neuer Suche
    } else {
      internalItems.value.push(...result.items) // Append beim Scroll
    }
    totalItems.value = result.total
    currentOffset.value = offset + result.items.length
  } catch (e) {
    console.error('Asset fetch failed:', e)
  } finally {
    isLoadingAssets.value = false
  }
}

// Methods
const handleFocus = () => {
  isSearchActive.value = true
  showSuggestions.value = true
  selectedIndex.value = -1
}

const handleBlur = () => {
  // Delay to allow for click events on suggestions
  setTimeout(() => {
    isSearchActive.value = false
    showSuggestions.value = false
    selectedIndex.value = -1
  }, 150)
}

/*const handleInput = () => {
  selectedIndex.value = -1
  lastIndex.value = 10; // Reset beim Tippen
  infiniteScrollKey.value++; // Force re-render
  if (!showSuggestions.value) {
    showSuggestions.value = true
  }
}*/

function handleInput() {
  if (!props.fetchItems) {
    // Alter Modus: lokale Filterung
    selectedIndex.value = -1
    lastIndex.value = 10
    return
  }
  // Neuer Modus: Debounced Server-Suche
  if (searchDebounceTimer.value) clearTimeout(searchDebounceTimer.value)
  searchDebounceTimer.value = window.setTimeout(() => {
    currentOffset.value = 0
    loadPage(0, localModel.value || '')
  }, 300)
}

const handleClick = (event: any) => {
  event.preventDefault();
  event.stopPropagation();
  props.onSetting();
}

const navigateDown = () => {
  if (selectedIndex.value < filteredAssets.value.length - 1) {
    selectedIndex.value++
    scrollToSelected()
  }
}

const navigateUp = () => {
  if (selectedIndex.value > 0) {
    selectedIndex.value--
    scrollToSelected()
  } else {
    selectedIndex.value = -1
  }
}

const scrollToSelected = () => {
  nextTick(() => {
    const dropdown = document.querySelector('.suggestions-dropdown')
    const highlighted = dropdown?.querySelector('.item-highlighted')
    if (highlighted) {
      highlighted.scrollIntoView({ block: 'nearest' })
    }
  })
}

const selectItem = () => {
  if (selectedIndex.value >= 0 && selectedIndex.value < filteredAssets.value.length) {
    selectAsset(filteredAssets.value[selectedIndex.value])
    props.onSubmit();
  }
}

const selectAsset = (asset: any) => {
  localModel.value = asset.title
  for (const key of [props.assetKeys].flat()) {
    const val = asset[key]
    if (val != null) model.value[key] = `=${val}`

  }
  showSuggestions.value = false
  isSearchActive.value = false
  selectedIndex.value = -1
  props.onSubmit();
}

const closeSuggestions = () => {
  showSuggestions.value = false
  isSearchActive.value = false
  selectedIndex.value = -1
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement;
  const scrollTop = element.scrollTop;
  const scrollHeight = element.scrollHeight;
  const clientHeight = element.clientHeight;

  const threshold = 50;
  const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;

  /*  if (isNearBottom) {
      lastIndex.value += 10;
    }*/

  if (isNearBottom && hasMoreItems.value && !isLoadingAssets.value) {
    loadPage(currentOffset.value, localModel.value || '')
  }
}

function highlightMatch(text: string, search: string | null): string {
  if (!search || !text) return text
  text = String(text)
  const regex = new RegExp(`(${escapeRegex(search)})`, 'gi')
  return text.replace(regex, '<mark class="search-highlight">$1</mark>')
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["search-wrapper", { 'search-active': isSearchActive.value }])
    }, [
      _createVNode(_component_v_text_field, {
        modelValue: localModel.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localModel).value = $event)),
        class: _normalizeClass([
          'search-input',
          { 'input-active': isSearchActive.value }
        ]),
        placeholder: _ctx.placeholder || 'Assets suchen...',
        variant: "outlined",
        density: "comfortable",
        clearable: "",
        "hide-details": "",
        rounded: "",
        onFocus: handleFocus,
        onBlur: handleBlur,
        onInput: handleInput,
        onKeydown: [
          _withKeys(_withModifiers(navigateDown, ["prevent"]), ["arrow-down"]),
          _withKeys(_withModifiers(navigateUp, ["prevent"]), ["arrow-up"]),
          _withKeys(_withModifiers(selectItem, ["prevent"]), ["enter"]),
          _withKeys(closeSuggestions, ["escape"])
        ]
      }, {
        "prepend-inner": _withCtx(() => [
          _createVNode(_component_v_icon, {
            color: "grey-darken-1",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (props.onSubmit()))
          }, {
            default: _withCtx(() => _cache[2] || (_cache[2] = [
              _createTextVNode("mdi-magnify")
            ])),
            _: 1,
            __: [2]
          })
        ]),
        "append-inner": _withCtx(() => [
          _createVNode(_component_v_btn, {
            icon: "",
            size: "small",
            variant: "text",
            class: "filter-button bg-secondary",
            onMousedown: handleClick,
            onClick: handleClick
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, null, {
                default: _withCtx(() => _cache[3] || (_cache[3] = [
                  _createTextVNode("mdi-tune")
                ])),
                _: 1,
                __: [3]
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue", "class", "placeholder", "onKeydown"]),
      (showSuggestions.value && filteredAssets.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "suggestions-dropdown",
            onScroll: handleScroll
          }, [
            _createVNode(_component_v_list, {
              density: "compact",
              class: "suggestions-list"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredAssets.value, (asset, index) => {
                  return (_openBlock(), _createBlock(_component_v_list_item, {
                    key: asset.id,
                    class: _normalizeClass({ 'item-highlighted': selectedIndex.value === index }),
                    onClick: ($event: any) => (selectAsset(asset)),
                    onMouseenter: ($event: any) => (selectedIndex.value = index)
                  }, {
                    prepend: _withCtx(() => [
                      _createVNode(_component_v_avatar, {
                        size: "32",
                        class: _normalizeClass(asset.is_safety ? 'bg-safetyBtnColor' : 'asset-avatar')
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(asset.prependIcon ?? 'mdi-cube-outline'), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, 1032, ["class"])
                    ]),
                    append: _withCtx(() => [
                      (asset.chip)
                        ? (_openBlock(), _createBlock(_component_v_chip, {
                            key: 0,
                            color: asset.chip_color,
                            size: "x-small",
                            variant: "flat"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(asset.chip), 1)
                            ]),
                            _: 2
                          }, 1032, ["color"]))
                        : _createCommentVNode("", true)
                    ]),
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_2, [
                        _createVNode(_component_v_list_item_title, { class: "asset-title d-flex align-center gap-1" }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", {
                              innerHTML: highlightMatch(asset.title, localModel.value)
                            }, null, 8, _hoisted_3),
                            (asset.matchedBy === 'id')
                              ? (_openBlock(), _createBlock(_component_v_chip, {
                                  key: 0,
                                  size: "x-small",
                                  color: "primary",
                                  variant: "tonal",
                                  class: "ml-1"
                                }, {
                                  default: _withCtx(() => _cache[4] || (_cache[4] = [
                                    _createTextVNode(" ID ")
                                  ])),
                                  _: 1,
                                  __: [4]
                                }))
                              : _createCommentVNode("", true)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_component_v_list_item_subtitle, { class: "asset-subtitle d-flex align-center gap-1" }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", {
                              innerHTML: highlightMatch(asset.subtitle, localModel.value)
                            }, null, 8, _hoisted_4),
                            (asset.matchedBy === 'title')
                              ? (_openBlock(), _createBlock(_component_v_chip, {
                                  key: 0,
                                  size: "x-small",
                                  color: "secondary",
                                  variant: "tonal",
                                  class: "ml-1"
                                }, {
                                  default: _withCtx(() => _cache[5] || (_cache[5] = [
                                    _createTextVNode(" Name ")
                                  ])),
                                  _: 1,
                                  __: [5]
                                }))
                              : _createCommentVNode("", true)
                          ]),
                          _: 2
                        }, 1024)
                      ])
                    ]),
                    _: 2
                  }, 1032, ["class", "onClick", "onMouseenter"]))
                }), 128))
              ]),
              _: 1
            })
          ], 32))
        : _createCommentVNode("", true),
      (showSuggestions.value && filteredAssets.value.length === 0 && localModel.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(_component_v_list, { density: "compact" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_list_item, null, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_v_icon, { color: "grey" }, {
                      default: _withCtx(() => _cache[6] || (_cache[6] = [
                        _createTextVNode("mdi-magnify-close")
                      ])),
                      _: 1,
                      __: [6]
                    })
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, { class: "text-grey" }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Keine Ergebnisse für \"" + _toDisplayString(localModel.value) + "\" ", 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ], 2)
  ]))
}
}

})